import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import api from '../services/api';
import useAuthStore from '../stores/authStore';
import useLang from '../hooks/useLang';

const fmt = v => new Intl.NumberFormat('sw-TZ').format(v || 0);

export default function Changiwa() {
  const { T } = useLang();
  const { user } = useAuthStore();
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
  const [searchParams] = useSearchParams();

  const [groups, setGroups] = useState([]);
  const [groupId, setGroupId] = useState(searchParams.get('group_id') || user?.group_id || '');
  const [events, setEvents] = useState([]);
  const [types, setTypes] = useState([]);
  const [members, setMembers] = useState([]);
  const [myChangiaGiven, setMyChangiaGiven] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ changiwa_type_id: '', title: '', beneficiary_name: '', beneficiary_member_id: '', event_date: '', amount_per_member: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (user?.role === 'superadmin') api.get('/groups').then(r => setGroups(r.data));
  }, [user]);

  useEffect(() => {
    if (!groupId) return;
    api.get(`/groups/${groupId}/changiwa-types`).then(r => setTypes(r.data));
    api.get(`/members?group_id=${groupId}`).then(r => setMembers(r.data));
    loadEvents();
    if (!isAdmin) {
      api.get(`/transactions?group_id=${groupId}&type=Changia`).then(r => setMyChangiaGiven(r.data)).catch(() => setMyChangiaGiven([]));
    }
  }, [groupId]);

  const myChangiaReceived = !isAdmin && user?.member_id
    ? events.filter(ev => +ev.beneficiary_member_id === +user.member_id)
    : [];

  const loadEvents = () => {
    if (!groupId) return;
    setLoading(true);
    api.get(`/changiwa-events?group_id=${groupId}`)
      .then(r => setEvents(r.data))
      .finally(() => setLoading(false));
  };

  const handleTypeChange = (typeId) => {
    const t = types.find(x => +x.id === +typeId);
    setForm(f => ({ ...f, changiwa_type_id: typeId, amount_per_member: t?.amount || '' }));
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      await api.post('/changiwa-events', { ...form, group_id: groupId });
      setShowModal(false);
      setForm({ changiwa_type_id: '', title: '', beneficiary_name: '', beneficiary_member_id: '', event_date: '', amount_per_member: '', notes: '' });
      loadEvents();
    } catch (err) {
      setError(err.response?.data?.error || T.changiwa.errCreate);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, title) => {
    if (!confirm(`Futa tukio "${title}"? Malipo yote yatafutwa.`)) return;
    await api.delete(`/changiwa-events/${id}`);
    loadEvents();
  };

  const statusBadge = (ev) => {
    if (ev.status === 'closed') return <span className="px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-500">Imefungwa</span>;
    const pct = ev.total_members > 0 ? Math.round((ev.paid_count / ev.total_members) * 100) : 0;
    const color = pct === 100 ? 'bg-green-100 text-green-700' : pct > 50 ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-600';
    return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${color}`}>{pct}% wmelipa</span>;
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl font-bold text-gray-800">{T.changiwa.title}</h1>
        {isAdmin && groupId && types.length > 0 && (
          <button onClick={() => setShowModal(true)}
            className="bg-green-700 hover:bg-green-800 text-white px-4 py-2 rounded-lg text-sm font-medium">
            {T.changiwa.addEvent}
          </button>
        )}
      </div>

      {user?.role === 'superadmin' && (
        <select value={groupId} onChange={e => setGroupId(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-green-500 focus:outline-none">
          <option value="">{T.common.selectGroupOption}</option>
          {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
        </select>
      )}

      {isAdmin && groupId && types.length === 0 && !loading && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">
          {T.changiwa.noTypes} <Link to={`/groups/${groupId}/settings`} className="underline font-medium">{T.changiwa.goSettings}</Link>
        </div>
      )}

      {!isAdmin && groupId && !loading && (
        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h2 className="font-bold text-gray-800 text-base mb-3">🤝 Nilizowachangia Wenzangu</h2>
            {myChangiaGiven.length === 0 ? (
              <p className="text-sm text-gray-400">Hujawahi kumchangia mwanachama mwenzako bado.</p>
            ) : (
              <div className="space-y-2">
                {myChangiaGiven.map(t => (
                  <div key={t.id} className="flex items-center justify-between rounded-lg bg-purple-50 px-3 py-2 text-sm">
                    <div>
                      <p className="font-medium text-gray-800">{(t.notes || '').replace(/^Changia:\s*/, '') || 'Changia'}</p>
                      <p className="text-xs text-gray-400">{new Date(t.transaction_date).toLocaleDateString('sw-TZ')}</p>
                    </div>
                    <span className="font-bold text-purple-700">{fmt(t.amount)} TZS</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h2 className="font-bold text-gray-800 text-base mb-3">🎁 Ambazo Nimechangiwa</h2>
            {myChangiaReceived.length === 0 ? (
              <p className="text-sm text-gray-400">Hakuna tukio lolote lililowekwa kwa ajili yako bado.</p>
            ) : (
              <div className="space-y-2">
                {myChangiaReceived.map(ev => (
                  <Link key={ev.id} to={`/changiwa/${ev.id}`}
                    className="flex items-center justify-between rounded-lg bg-emerald-50 px-3 py-2 text-sm hover:bg-emerald-100">
                    <div>
                      <p className="font-medium text-gray-800">{ev.title}</p>
                      <p className="text-xs text-gray-400">{ev.type_name} · {ev.paid_count}/{ev.total_members} wamechangia</p>
                    </div>
                    <span className="font-bold text-emerald-700">{fmt(ev.total_collected)} TZS</span>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {!groupId ? (
        <div className="text-center py-16 text-gray-400">{T.changiwa.chooseGroup}</div>
      ) : loading ? (
        <div className="text-center py-16 text-gray-500">{T.common.loading}</div>
      ) : (
        <div className="space-y-3">
          {events.map(ev => (
            <div key={ev.id} className="bg-white rounded-xl border border-gray-200 p-5">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-bold text-gray-800 text-base">{ev.title}</h3>
                    <span className="px-2 py-0.5 rounded-full text-xs bg-blue-50 text-blue-600 font-medium">
                      {ev.type_name}
                    </span>
                    {statusBadge(ev)}
                  </div>
                  {ev.beneficiary_name && (
                    <p className="text-sm text-gray-500 mt-0.5">{T.changiwa.beneficiary}: {ev.beneficiary_name}</p>
                  )}
                  <p className="text-xs text-gray-400 mt-1">
                    {ev.event_date ? new Date(ev.event_date).toLocaleDateString('sw-TZ') : ''}
                    {ev.notes && ` — ${ev.notes}`}
                  </p>
                </div>
                <div className="text-right">
                  <div className="text-xl font-bold text-green-700">{fmt(ev.total_collected)} TZS</div>
                  <div className="text-xs text-gray-400">
                    {ev.paid_count}/{ev.total_members} {T.changiwa.paid} × {fmt(ev.amount_per_member)}
                  </div>
                </div>
              </div>

              {+ev.disbursed_amount > 0 && (
                <div className="mt-3 bg-orange-50 border border-orange-100 rounded-lg px-3 py-2 text-xs text-orange-700">
                  💸 {T.changiwa.disbursed}: <strong>{fmt(ev.disbursed_amount)} TZS</strong> — {T.changiwa.disbursedNote}
                </div>
              )}

              <div className="flex gap-2 mt-3">
                <Link to={`/changiwa/${ev.id}`}
                  className="bg-green-700 hover:bg-green-800 text-white px-4 py-1.5 rounded-lg text-xs font-medium">
                  {T.changiwa.viewPayments}
                </Link>
                {isAdmin && ev.status === 'open' && (
                  <button onClick={async () => { await api.post(`/changiwa-events/${ev.id}/close`); loadEvents(); }}
                    className="border border-gray-300 text-gray-600 px-4 py-1.5 rounded-lg text-xs hover:bg-gray-50">
                    {T.changiwa.close}
                  </button>
                )}
                {isAdmin && (
                  <button onClick={() => handleDelete(ev.id, ev.title)}
                    className="text-red-400 hover:text-red-600 text-xs px-2 py-1.5 ml-auto">
                    {T.common.delete}
                  </button>
                )}
              </div>
            </div>
          ))}

          {events.length === 0 && (
            <div className="text-center py-16 text-gray-400 bg-white rounded-xl border border-gray-200">
              {T.changiwa.noEvents}
              {isAdmin && <><br /><span className="text-sm">{T.changiwa.noEventsHint}</span></>}
            </div>
          )}
        </div>
      )}

      {/* Create modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-5">{T.changiwa.newEvent}</h2>
            {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm mb-4">{error}</div>}
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{T.changiwa.typeLabel} *</label>
                <select required value={form.changiwa_type_id} onChange={e => handleTypeChange(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-500">
                  <option value="">{T.changiwa.typeSelect}</option>
                  {types.map(t => (
                    <option key={t.id} value={t.id}>{t.name} ({fmt(t.amount)} TZS)</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{T.changiwa.title_label} *</label>
                <input required type="text" value={form.title}
                  onChange={e => setForm({ ...form, title: e.target.value })}
                  placeholder={T.changiwa.titlePlaceholder}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{T.changiwa.beneficiary}</label>
                  <select value={form.beneficiary_member_id}
                    onChange={e => {
                      const m = members.find(mm => String(mm.id) === e.target.value);
                      setForm({ ...form, beneficiary_member_id: e.target.value, beneficiary_name: m?.name || '' });
                    }}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-500">
                    <option value="">{T.changiwa.beneficiarySelect}</option>
                    {members.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{T.changiwa.eventDate}</label>
                  <input type="date" value={form.event_date}
                    onChange={e => setForm({ ...form, event_date: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{T.changiwa.amountPerMember}</label>
                <input type="number" min="0" value={form.amount_per_member}
                  onChange={e => setForm({ ...form, amount_per_member: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
                <p className="text-xs text-gray-400 mt-1">{T.changiwa.amountNote.replace('{n}', members.length)}: {fmt((form.amount_per_member || 0) * members.length)} TZS</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{T.changiwa.notes}</label>
                <input type="text" value={form.notes}
                  onChange={e => setForm({ ...form, notes: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => { setShowModal(false); setError(''); }}
                  className="flex-1 border border-gray-300 text-gray-700 py-2.5 rounded-lg text-sm font-medium hover:bg-gray-50">{T.common.cancel}</button>
                <button type="submit" disabled={saving}
                  className="flex-1 bg-green-700 text-white py-2.5 rounded-lg text-sm font-medium hover:bg-green-800 disabled:opacity-60">
                  {saving ? T.changiwa.creating : T.changiwa.createEvent}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
